import { useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';

import {
  mesesHasta,
  periodoMes,
  resumenPeriodo,
  semanasHasta,
  sumarDias,
  ultimaSemanaCerrada,
  ultimoMesCerrado,
} from './diario';
import type { DiaCorte, Periodo, ResumenDiario } from './diario';
import { useCorteDiario, useMovimientosDiarios } from './useDiario';

export type TipoDiario = 'semana' | 'mes';

/** Periodos de historia con los que se compara (lo habitual). */
const HISTORIA = { semana: 8, mes: 6 } as const;

export type DatosDiario = {
  tipo: TipoDiario;
  /** El periodo que se ve. */
  periodo: Periodo;
  /** Mes del periodo ('yyyy-MM'); en la semana, el de su domingo. */
  mes: string;
  /** Periodos de historia + el que se ve (el último). */
  periodos: Periodo[];
  corte: DiaCorte | null;
  /** Se puede ir al siguiente (no se pasa del periodo en curso). */
  haySiguiente: boolean;
  resumen: ResumenDiario | null;
  isLoading: boolean;
};

/**
 * Datos del «Diario de a bordo». `desplazamiento` cuenta periodos desde el
 * último cerrado: 0 = el último cerrado, 1 = el que está en curso, −1 = el
 * anterior…
 */
export function useResumenDiario(
  tipo: TipoDiario,
  desplazamiento = 0,
): DatosDiario {
  const hoy = monthUtils.currentDay();
  const [corte] = useCorteDiario();
  const corteMes = tipo === 'mes' ? corte : null;

  const { periodos, mes } = useMemo(() => {
    if (tipo === 'semana') {
      const base = ultimaSemanaCerrada(hoy);
      const lunes = sumarDias(base.desde, 7 * desplazamiento);
      const lista = semanasHasta(lunes, HISTORIA.semana + 1);
      const ultima = lista[lista.length - 1];
      return { periodos: lista, mes: ultima.hasta.slice(0, 7) };
    }
    const base = ultimoMesCerrado(hoy, corteMes);
    const elegido = monthUtils.addMonths(base, desplazamiento);
    const lista = mesesHasta(elegido, HISTORIA.mes + 1).map(m =>
      periodoMes(m, corteMes),
    );
    return { periodos: lista, mes: elegido };
  }, [tipo, hoy, desplazamiento, corteMes]);

  const periodo = periodos[periodos.length - 1];
  const { movimientos, categorias, isLoading } = useMovimientosDiarios(
    periodos[0].desde,
    periodo.hasta,
  );

  const resumen = useMemo(
    () =>
      isLoading ? null : resumenPeriodo(movimientos, periodos, categorias, hoy),
    [isLoading, movimientos, periodos, categorias, hoy],
  );

  return {
    tipo,
    periodo,
    mes,
    periodos,
    corte: corteMes,
    haySiguiente: desplazamiento < 1,
    resumen,
    isLoading,
  };
}
